import { useFragment } from "@/generated";
import { UpdateVocabCardDocument, VocabCardFieldsFragmentDoc } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

interface UpdateVocabCardInput {
  word: string;
  definition: string;
  example: string;
  partOfSpeech: string;
  imageFile?: File | null;
  audioFile?: File | null;
}

export const useUpdateVocabCard = () => {
  const [updateVocabCardMutation, { loading }] = useMutation(UpdateVocabCardDocument, {
    update(cache, { data }) {
      if (!data?.updateVocabCard) return;

      const updatedVocabCard = useFragment(VocabCardFieldsFragmentDoc, data.updateVocabCard);

      const id = cache.identify(updatedVocabCard);

      if (!id) return;

      cache.modify({
        fields: {
          getVocabCards(existingData, { storeFieldName, readField }) {
            if (!storeFieldName.includes('"searchText":null') || !existingData?.items) {
              return existingData;
            }

            const updatedRef = { __ref: id };

            return {
              ...existingData,
              items: [
                updatedRef,
                ...existingData.items.filter(
                  (ref: Reference) => updatedVocabCard.id !== readField("id", ref)
                ),
              ],
              total: existingData.total + 1,
            };
          },
        },
      });
    },
  });

  const updateVocabCard = async (vocabCardId: string, input: UpdateVocabCardInput) => {
    return withToast(
      async () => {
        await updateVocabCardMutation({
          variables: {
            id: vocabCardId,
            input: {
              word: input.word,
              definition: input.definition,
              example: input.example,
              partOfSpeech: input.partOfSpeech,
            },
            // Only send image/audio if new files were selected
            ...(input.imageFile && { image: input.imageFile }),
            ...(input.audioFile && { audio: input.audioFile }),
          },
        });
      },
      {
        success: "Successfully updated vocabulary card",
        loading: "Updating vocabulary card...",
        error: "Failed to update vocabulary card",
      },
      "Vocab-card-update-toast"
    );
  };

  return {
    loading,
    updateVocabCard,
  };
};
